import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, Clock, ShieldCheck, Lock, XIcon, Mail, Download, Sparkles, Heart, ListChecks, BookOpen, Coffee, Sun, Moon } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
// Meta Pixel
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}
const useMetaPixel = () => {
  useEffect(() => {
    const f = window as any;
    if (f.fbq) return;
    const n: any = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(t);
    window.fbq('init', '1154453463472983');
    window.fbq('track', 'PageView');
  }, []);
};
const fireAddToCart = () => {
  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: '24-Hour Bloat Protocol',
      content_type: 'product',
      value: 27.0,
      currency: 'USD'
    });
  }
};
const SHOPIFY_URL = 'https://www.foundationalgut.lat/cart/45550837170278:1';
const CTAButton = ({
  className = '',
  href = '#offer',
  onClick,
  label = 'Get Instant Access'
}: {
  className?: string;
  href?: string;
  onClick?: () => void;
  label?: string;
}) => <div className={`flex flex-col items-center ${className}`} data-id="element-1">
    <a href={href} onClick={onClick} className="group relative inline-flex items-center justify-center gap-3 bg-yellow text-dark font-bold text-lg md:text-xl px-8 py-5 rounded-sm uppercase tracking-wider hover:bg-[#ccff00] transition-all duration-300 shadow-[0_0_30px_rgba(229,255,0,0.15)] hover:shadow-[0_0_40px_rgba(229,255,0,0.3)] w-full md:w-auto overflow-hidden" data-id="element-2">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-3" />
      <span data-id="element-4">{label}</span>
      <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-5" />
    </a>
    <div className="flex items-center gap-2 mt-3 text-sm text-neutral-400" data-id="element-6">
      <Lock className="w-4 h-4" data-id="element-7" />
      <span data-id="element-8">Digital download • Instant delivery • One-time purchase</span>
    </div>
  </div>;
// Legal Modal
const LegalModal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => <AnimatePresence data-id="element-9">
    {isOpen && <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-10">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-11" />
        <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} className="relative bg-neutral-900 border border-neutral-700 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-12">
          <div className="flex items-center justify-between p-6 border-b border-neutral-800" data-id="element-13">
            <h3 className="font-serif text-2xl text-white" data-id="element-14">{title}</h3>
            <button onClick={onClose} className="text-neutral-400 hover:text-white transition-colors p-1" data-id="element-15">
              <XIcon className="w-6 h-6" data-id="element-16" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-neutral-300 leading-relaxed space-y-4 text-sm" data-id="element-17">
            {children}
          </div>
        </motion.div>
      </motion.div>}
  </AnimatePresence>;
// Sticky Mobile Bar
const StickyMobileBar = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <AnimatePresence data-id="element-18">
      {isVisible && <motion.div initial={{
      y: 100
    }} animate={{
      y: 0
    }} exit={{
      y: 100
    }} className="fixed bottom-0 left-0 w-full z-40 md:hidden bg-dark/95 backdrop-blur border-t border-neutral-800 p-4 pb-safe" data-id="element-19">
          <div className="flex items-center justify-between gap-4" data-id="element-20">
            <div className="flex flex-col" data-id="element-21">
              <span className="text-neutral-500 text-xs uppercase tracking-wider" data-id="element-22">
                Today
              </span>
              <span className="text-white font-serif text-2xl leading-none" data-id="element-23">
                $27
              </span>
            </div>
            <a href="#offer" className="bg-yellow text-dark font-bold px-6 py-3 rounded-sm uppercase tracking-wider text-sm flex-1 text-center shadow-[0_0_15px_rgba(229,255,0,0.2)]" data-id="element-24">
              Get Instant Access →
            </a>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
export function App() {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  useMetaPixel();
  return <div className="min-h-screen bg-dark font-sans selection:bg-cyan selection:text-dark" data-id="element-25">
      <StickyMobileBar data-id="element-26" />

      {/* ═══════════════════════════════════════════
              1. HERO
          ═══════════════════════════════════════════ */}
      <section className="relative pt-12 pb-16 md:pt-24 md:pb-24 overflow-hidden border-b border-neutral-800" data-id="element-27">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan/10 blur-[120px] rounded-full pointer-events-none" data-id="element-28" />
        <div className="container mx-auto px-6 max-w-6xl relative z-10" data-id="element-29">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-30">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-31">
              <div className="inline-block border border-cyan/30 bg-cyan/10 text-cyan px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide mb-6" data-id="element-32">
                THE 24-HOUR BLOAT PROTOCOL
              </div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-white mb-6" data-id="element-33">
                Support <span className="text-cyan" data-id="element-34">Digestive Comfort</span> in
                24 Hours
              </h1>
              <p className="text-xl md:text-2xl text-neutral-300 leading-relaxed mb-8" data-id="element-35">
                A guided digital protocol for occasional bloating, post-meal
                heaviness, and uncomfortable "off" days. Simple, structured, and
                easy to follow at home.
              </p>

              <ul className="space-y-3 mb-10" data-id="element-36">
                {['Designed for occasional bloating', 'Step-by-step 24-hour routine', 'No extreme detoxing or complicated plans'].map((bullet, i) => <li key={i} className="flex items-start gap-3 text-neutral-300" data-id="element-37">
                    <CheckCircle2 className="w-5 h-5 text-cyan shrink-0 mt-0.5" data-id="element-38" />
                    <span data-id="element-39">{bullet}</span>
                  </li>)}
              </ul>

              <CTAButton className="items-start" data-id="element-40" />

              <p className="text-xs text-neutral-500 mt-6 italic max-w-md" data-id="element-41">
                For occasional digestive discomfort only — not medical
                treatment.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-42">
              <div className="absolute -inset-4 bg-cyan/20 rounded-3xl blur-3xl animate-pulse" data-id="element-43" />
              <div className="absolute -inset-1 bg-gradient-to-tr from-cyan/30 via-yellow/10 to-cyan/20 rounded-2xl blur-xl" data-id="element-44" />
              <img src="https://cdn.magicpatterns.com/uploads/pmDY3YFeFRMaNMgmFcGXCS/the_24_hours.png" alt="24-Hour Bloat Protocol on Mobile Device" className="relative z-10 w-full h-auto rounded-2xl shadow-[0_0_60px_rgba(0,229,204,0.25)] border-2 border-cyan/30 brightness-110 contrast-105" data-id="element-45" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              2. WHAT THIS IS
          ═══════════════════════════════════════════ */}
      <section className="py-24 bg-neutral-950" data-id="element-46">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-47">
          <FadeIn data-id="element-48">
            <h2 className="font-serif text-4xl md:text-5xl text-white text-center mb-10 leading-tight" data-id="element-49">
              A calmer, simpler plan for the days your stomach feels off
            </h2>
            <div className="space-y-6 text-lg text-neutral-300 leading-relaxed" data-id="element-50">
              <p data-id="element-51">
                If you ever feel heavy, puffy, or unusually uncomfortable after
                meals, this protocol gives you a clear 24-hour routine to follow
                — without random guessing, extreme cleanses, or complicated meal
                rules.
              </p>
              <p data-id="element-52">
                It's designed for people who want a more structured response to
                occasional bloating, especially on workdays, travel days, or
                after meals that leave them feeling uncomfortable.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              3. WHAT YOU GET
          ═══════════════════════════════════════════ */}
      <section className="py-24 bg-dark border-y border-neutral-800" data-id="element-53">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-54">
          <FadeIn data-id="element-55">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-56">
              Inside the{' '}
              <span className="text-cyan" data-id="element-57">24-Hour Bloat Protocol</span>
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-58">
            {[{
            icon: ListChecks,
            title: 'The full 24-hour step-by-step routine'
          }, {
            icon: BookOpen,
            title: 'A simple food guide for bloated days'
          }, {
            icon: Coffee,
            title: 'A hydration and comfort checklist'
          }, {
            icon: Sparkles,
            title: 'A "what to avoid today" quick-reference page'
          }, {
            icon: Sun,
            title: 'A morning / afternoon / evening action plan'
          }, {
            icon: Download,
            title: 'Instant digital access after checkout'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-59">
                <div className="bg-neutral-900 border border-neutral-800 p-6 rounded-xl flex items-start gap-4 h-full hover:border-cyan/40 transition-colors" data-id="element-60">
                  <div className="w-12 h-12 bg-cyan/10 rounded-lg flex items-center justify-center shrink-0" data-id="element-61">
                    <item.icon className="w-6 h-6 text-cyan" data-id="element-62" />
                  </div>
                  <p className="text-white text-lg leading-relaxed pt-2" data-id="element-63">
                    {item.title}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              4. WHO IT'S FOR
          ═══════════════════════════════════════════ */}
      <section className="py-24 bg-neutral-950" data-id="element-64">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-65">
          <FadeIn data-id="element-66">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-67">
              Made for people who deal with:
            </h2>
            <div className="bg-dark border border-neutral-800 rounded-3xl p-8 md:p-12" data-id="element-68">
              <ul className="space-y-5" data-id="element-69">
                {['Bloating after meals', 'Heavy or puffy-feeling days', 'Uncomfortable midsection tightness', 'Occasional digestive discomfort', 'Days when you want a gentler reset, not another random detox'].map((text, i) => <li key={i} className="flex items-start gap-4 text-lg text-neutral-300" data-id="element-70">
                    <Heart className="w-6 h-6 text-cyan shrink-0 mt-0.5" data-id="element-71" />
                    <span data-id="element-72">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              5. WHY PEOPLE WANT THIS
          ═══════════════════════════════════════════ */}
      <section className="py-24 bg-dark border-y border-neutral-800" data-id="element-73">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-74">
          <FadeIn data-id="element-75">
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-10 text-center leading-tight" data-id="element-76">
              Because guessing makes{' '}
              <span className="text-cyan" data-id="element-77">bloated days worse</span>
            </h2>
            <div className="space-y-6 text-lg text-neutral-300 leading-relaxed" data-id="element-78">
              <p data-id="element-79">
                Most people don't need more confusion. They need a clear plan.
              </p>
              <p data-id="element-80">
                Instead of trying five different things in one day, this
                protocol gives you one structured routine to follow from morning
                to night — so you can feel more in control, more consistent, and
                less overwhelmed on the days your stomach feels off.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              6. WHY CLINICAL RESET
          ═══════════════════════════════════════════ */}
      <section className="py-24 bg-neutral-950" data-id="element-81">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-82">
          <FadeIn data-id="element-83">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-84">
              Why <span className="text-cyan" data-id="element-85">Clinical Reset</span> feels
              different
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-86">
            {[{
            title: 'Structured, not extreme',
            desc: 'A clear path forward instead of harsh restrictions.'
          }, {
            title: 'Clear, not overwhelming',
            desc: 'Simple steps that fit into your day.'
          }, {
            title: 'Designed for real-life use',
            desc: 'Built for workdays, travel days, and busy schedules.'
          }, {
            title: 'Mobile-friendly format',
            desc: 'Read it from your phone — no printing needed.'
          }, {
            title: 'Built around simplicity',
            desc: 'No hype, no gimmicks, no complicated rules.'
          }, {
            title: 'One-time purchase',
            desc: 'No subscriptions, no recurring fees, no upsells.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-87">
                <div className="bg-dark border border-neutral-800 p-8 rounded-2xl h-full hover:border-cyan/40 transition-colors" data-id="element-88">
                  <div className="w-10 h-10 bg-cyan/10 rounded-full flex items-center justify-center mb-5" data-id="element-89">
                    <CheckCircle2 className="w-5 h-5 text-cyan" data-id="element-90" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-91">
                    {item.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed" data-id="element-92">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              7. OFFER BOX
          ═══════════════════════════════════════════ */}
      <section className="py-24 relative overflow-hidden bg-dark" data-id="element-93">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[500px] bg-cyan/5 blur-[150px] rounded-full pointer-events-none" data-id="element-94" />
        <div className="container mx-auto px-6 max-w-3xl relative z-10" data-id="element-95">
          <FadeIn data-id="element-96">
            <div id="offer" className="bg-neutral-900 border-2 border-cyan/30 p-8 md:p-14 rounded-3xl text-center shadow-[0_0_50px_rgba(0,229,204,0.1)] scroll-mt-8" data-id="element-97">
              <div className="inline-block border border-cyan/30 bg-cyan/10 text-cyan px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide mb-6" data-id="element-98">
                ONE-TIME PURCHASE
              </div>
              <h2 className="font-serif text-4xl md:text-5xl mb-8 text-white" data-id="element-99">
                Get the 24-Hour Bloat Protocol
              </h2>

              <div className="mb-10" data-id="element-100">
                <p className="text-neutral-400 uppercase tracking-widest text-sm font-semibold mb-3" data-id="element-101">
                  Today
                </p>
                <p className="text-7xl font-serif text-white mb-3" data-id="element-102">
                  $27<span className="text-3xl text-neutral-500" data-id="element-103"> USD</span>
                </p>
                <p className="text-neutral-400 text-base" data-id="element-104">
                  One-time purchase. Instant access.
                </p>
              </div>

              <div className="mb-10 bg-dark border border-neutral-800 rounded-2xl p-6 text-left" data-id="element-105">
                <p className="text-neutral-500 uppercase tracking-widest text-xs font-bold mb-4 text-center" data-id="element-106">
                  What happens after purchase
                </p>
                <p className="text-neutral-300 text-center leading-relaxed" data-id="element-107">
                  You'll receive immediate access to your digital protocol and
                  instructions by email.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6 text-sm text-neutral-400" data-id="element-108">
                  <span className="flex items-center gap-2" data-id="element-109">
                    <Lock className="w-4 h-4 text-cyan" data-id="element-110" /> Secure checkout
                  </span>
                  <span className="hidden sm:block text-neutral-700" data-id="element-111">•</span>
                  <span className="flex items-center gap-2" data-id="element-112">
                    <Mail className="w-4 h-4 text-cyan" data-id="element-113" /> Email delivery
                  </span>
                  <span className="hidden sm:block text-neutral-700" data-id="element-114">•</span>
                  <span className="flex items-center gap-2" data-id="element-115">
                    <Download className="w-4 h-4 text-cyan" data-id="element-116" /> Instant download
                  </span>
                </div>
              </div>

              <CTAButton href={SHOPIFY_URL} onClick={fireAddToCart} data-id="element-117" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              8. GUARANTEE
          ═══════════════════════════════════════════ */}
      <section className="py-20 bg-neutral-950" data-id="element-118">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-119">
          <FadeIn data-id="element-120">
            <div className="border border-cyan/20 bg-dark p-8 md:p-10 rounded-3xl text-center" data-id="element-121">
              <ShieldCheck className="w-16 h-16 text-cyan mx-auto mb-5" data-id="element-122" />
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-4" data-id="element-123">
                7-Day Simple Guarantee
              </h2>
              <p className="text-lg text-neutral-300 leading-relaxed max-w-xl mx-auto" data-id="element-124">
                Try the protocol. If it's not a fit for you, contact us within 7
                days and we'll help you with the next steps.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              9. FAQ
          ═══════════════════════════════════════════ */}
      <section className="py-24 bg-dark border-t border-neutral-800" data-id="element-125">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-126">
          <FadeIn data-id="element-127">
            <h2 className="font-serif text-4xl md:text-5xl text-center mb-16" data-id="element-128">
              Frequently Asked Questions
            </h2>
          </FadeIn>
          <div className="space-y-8" data-id="element-129">
            {[{
            q: 'Is this a supplement?',
            a: 'No. This is a digital protocol designed to help guide you through a 24-hour routine for occasional bloating and digestive discomfort.'
          }, {
            q: 'How do I receive it?',
            a: 'Immediately after checkout by email.'
          }, {
            q: 'Is this for everyday medical digestive issues?',
            a: 'No. This is for occasional bloated or heavy-feeling days only.'
          }, {
            q: 'Is this medical advice?',
            a: 'No. This product is educational and informational only.'
          }, {
            q: 'Who is this best for?',
            a: 'Adults who want a simple routine for occasional bloating, heaviness, or post-meal discomfort.'
          }, {
            q: 'What if my symptoms are severe or persistent?',
            a: 'Talk to a qualified healthcare professional. NIDDK says bloating and distention are common symptoms, but also recommends speaking with a doctor if symptoms bother you, change suddenly, or come with pain, constipation, diarrhea, or weight loss.'
          }].map((faq, i) => <FadeIn key={i} delay={i * 0.06} data-id="element-130">
                <div className="border-b border-neutral-800 pb-8" data-id="element-131">
                  <h4 className="text-xl font-bold text-white mb-3" data-id="element-132">{faq.q}</h4>
                  <p className="text-neutral-400 text-lg leading-relaxed" data-id="element-133">
                    {faq.a}
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <div className="mt-16 flex justify-center" data-id="element-134">
            <FadeIn data-id="element-135">
              <CTAButton href={SHOPIFY_URL} onClick={fireAddToCart} data-id="element-136" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
              DISCLAIMER STRIP
          ═══════════════════════════════════════════ */}
      <section className="py-10 bg-neutral-950 border-y border-neutral-800" data-id="element-137">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-138">
          <p className="text-neutral-500 text-sm text-center leading-relaxed" data-id="element-139">
            This product is educational and informational only. It is not
            medical advice and is not intended to diagnose, treat, cure, or
            prevent any disease. For severe or persistent symptoms, please
            consult a qualified healthcare professional.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-neutral-800 py-8 bg-dark pb-32 md:pb-8" data-id="element-140">
        <div className="container mx-auto px-6 max-w-6xl flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-141">
          <p className="text-neutral-500 text-sm" data-id="element-142">
            © {new Date().getFullYear()} Clinical Reset. All rights reserved.
          </p>
          <div className="flex items-center gap-6 flex-wrap justify-center" data-id="element-143">
            <button onClick={() => setShowDisclaimer(true)} className="text-neutral-500 hover:text-cyan text-sm transition-colors" data-id="element-144">
              Disclaimer
            </button>
            <button onClick={() => setShowTerms(true)} className="text-neutral-500 hover:text-cyan text-sm transition-colors" data-id="element-145">
              Terms & Conditions
            </button>
            <button onClick={() => setShowPrivacy(true)} className="text-neutral-500 hover:text-cyan text-sm transition-colors" data-id="element-146">
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <LegalModal isOpen={showDisclaimer} onClose={() => setShowDisclaimer(false)} title="Health & Medical Disclaimer" data-id="element-147">
        <p data-id="element-148">
          This product is educational and informational only. It is not intended
          to diagnose, treat, cure, or prevent any disease.
        </p>
        <p data-id="element-149">
          The information provided is not a substitute for professional medical
          advice, diagnosis, or treatment. Always seek the advice of a qualified
          healthcare provider with any questions you may have regarding a
          medical condition.
        </p>
        <p data-id="element-150">
          If you experience severe, sudden, or persistent symptoms — including
          pain, constipation, diarrhea, unexplained weight loss, or other
          concerning changes — please consult a healthcare professional
          immediately.
        </p>
        <p data-id="element-151">
          Individual results may vary. This product is intended for adults
          dealing with occasional bloating or digestive discomfort.
        </p>
      </LegalModal>

      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms & Conditions" data-id="element-152">
        <p data-id="element-153">
          <strong className="text-white" data-id="element-154">Last Updated:</strong> April 2026
        </p>
        <p data-id="element-155">
          By purchasing and using the 24-Hour Bloat Protocol, you agree to the
          following terms.
        </p>
        <p data-id="element-156">
          <strong className="text-white" data-id="element-157">1. Product Description</strong>
        </p>
        <p data-id="element-158">
          The 24-Hour Bloat Protocol is a digital informational product
          delivered as a PDF. It provides general guidance for occasional
          digestive discomfort and is not a medical treatment, prescription, or
          diagnosis.
        </p>
        <p data-id="element-159">
          <strong className="text-white" data-id="element-160">2. Educational Use Only</strong>
        </p>
        <p data-id="element-161">
          This product is for informational and educational purposes only. It
          does not replace professional medical advice. Always consult a
          qualified healthcare provider before making changes to your health
          routine.
        </p>
        <p data-id="element-162">
          <strong className="text-white" data-id="element-163">3. No Guarantees of Results</strong>
        </p>
        <p data-id="element-164">
          Individual results vary. We do not guarantee specific outcomes from
          following the protocol.
        </p>
        <p data-id="element-165">
          <strong className="text-white" data-id="element-166">4. Digital Delivery</strong>
        </p>
        <p data-id="element-167">You will receive immediate access via email after purchase.</p>
        <p data-id="element-168">
          <strong className="text-white" data-id="element-169">5. Refund Policy</strong>
        </p>
        <p data-id="element-170">
          If the protocol is not a fit, contact us within 7 days of purchase and
          we'll help with next steps.
        </p>
        <p data-id="element-171">
          <strong className="text-white" data-id="element-172">6. Intellectual Property</strong>
        </p>
        <p data-id="element-173">
          All content is protected by copyright. You may not reproduce,
          distribute, or share the content without written permission.
        </p>
        <p data-id="element-174">
          <strong className="text-white" data-id="element-175">7. Limitation of Liability</strong>
        </p>
        <p data-id="element-176">
          The creators are not liable for any adverse effects, damages, or
          consequences resulting from the use or misuse of this information.
        </p>
      </LegalModal>

      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" data-id="element-177">
        <p data-id="element-178">
          <strong className="text-white" data-id="element-179">Last Updated:</strong> April 2026
        </p>
        <p data-id="element-180">
          <strong className="text-white" data-id="element-181">1. Information We Collect</strong>
        </p>
        <p data-id="element-182">
          We collect only the information needed to process your purchase: name,
          email, and payment details. Payments are processed by secure
          third-party providers — we never store credit card information.
        </p>
        <p data-id="element-183">
          <strong className="text-white" data-id="element-184">2. How We Use Your Information</strong>
        </p>
        <p data-id="element-185">
          Your information is used solely to deliver your purchase and provide
          customer support. We do not sell or share your data.
        </p>
        <p data-id="element-186">
          <strong className="text-white" data-id="element-187">3. Cookies & Tracking</strong>
        </p>
        <p data-id="element-188">
          We use essential cookies and analytics tools (including Meta Pixel) to
          understand how visitors use our site and to deliver relevant ads. You
          can disable cookies in your browser settings.
        </p>
        <p data-id="element-189">
          <strong className="text-white" data-id="element-190">4. Data Security</strong>
        </p>
        <p data-id="element-191">
          We use industry-standard SSL encryption to protect your information.
        </p>
        <p data-id="element-192">
          <strong className="text-white" data-id="element-193">5. Your Rights</strong>
        </p>
        <p data-id="element-194">
          You may request access, correction, or deletion of your personal data
          at any time.
        </p>
        <p data-id="element-195">
          <strong className="text-white" data-id="element-196">6. Changes to This Policy</strong>
        </p>
        <p data-id="element-197">
          We may update this policy from time to time. Updates will be reflected
          on this page.
        </p>
      </LegalModal>
    </div>;
}